import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";

/** One-time waiver gate — shown the first time a Stranger tries to book a session. */
export const WaiverModal = ({ open, onOpenChange, onSigned }) => {
  const { setUser } = useAuth();
  const [doc, setDoc] = useState(null);
  const [fullName, setFullName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/content/legal/stranger-waiver")
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc({ title: "Participant Waiver", body: "By signing, you agree to participate voluntarily and release Stranger Sessions and its photographers from liability except in cases of gross negligence." }));
  }, [open]);

  const sign = async () => {
    setSigning(true);
    try {
      const { data } = await api.post("/auth/sign-waiver", { full_name: fullName, agreed });
      setUser(data);
      toast.success("Waiver signed");
      setFullName("");
      setAgreed(false);
      onSigned?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="waiver-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-extrabold uppercase">{doc?.title || "Participant Waiver"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs font-medium text-midnight-black/50 -mt-2">One-time — you won't need to sign this again for future sessions.</p>
        <div className="max-h-64 overflow-y-auto text-sm font-medium text-midnight-black/70 whitespace-pre-line border border-soft-gray rounded-xl p-4" data-testid="waiver-body">
          {doc?.body}
        </div>
        <label className="flex items-start gap-2 text-sm font-medium mt-2 cursor-pointer">
          <input type="checkbox" data-testid="waiver-agree-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-stranger-orange" />
          I agree to the terms above.
        </label>
        <Input
          data-testid="waiver-signature-input"
          placeholder="Type your full legal name to sign"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="mt-1"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full font-bold">
            Cancel
          </Button>
          <Button
            data-testid="waiver-sign-submit"
            disabled={!agreed || !fullName.trim() || signing}
            onClick={sign}
            className="rounded-full font-bold bg-stranger-orange hover:bg-orange-600 text-clean-white"
          >
            {signing ? "Signing..." : "SIGN & CONTINUE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
